import type { ReactElement } from 'react';
import React, { useContext } from 'react';
import { match } from 'ts-pattern';
import { DataElementId } from '../../../../constants/data-element-tracking-ids';
import {
  ArticlePreviewCard,
  ResourcePreviewCard,
} from '../resource-preview-card';
import { ResourceType } from '../../../../dotcms/dotcms-models/resources/resource-with-thumbnail/resource-type';
import { MediaType } from '../../../../component_library/components/Card/Card';
import type { IRelatedResourcesData } from '../../../../dotcms/dotcms-models/resources/dotcms-article';
import type { IResourceWithThumbnailData } from '../../../../dotcms/dotcms-models/resources/resource-with-thumbnail/resource-with-thumbnail';
import { GlobalTextVariablesContext } from '../../../../dotcms/contexts/global-text-variables-context';
import type { ILinkData } from '../../../../dotcms/dotcms-models/dotcms-link';

interface IProps {
  renderCardWrapper?: (children: ReactElement) => ReactElement;
  data: Partial<IRelatedResourcesData>;
  articleLink?: ILinkData;
  documentsThumbnail?: string;
  atLeastHalfMagicLoggedIn: boolean;
}

const ResourcesPreviewCards: React.FC<IProps> = ({
  renderCardWrapper,
  data,
  articleLink,
  documentsThumbnail,
  atLeastHalfMagicLoggedIn,
}) => {
  const { learnMoreLinkText } = useContext(GlobalTextVariablesContext);
  const articleTypeArticles = (data.articlesAsArticles ?? []).filter(
    ({ authenticationRequiredToView }) =>
      atLeastHalfMagicLoggedIn || !authenticationRequiredToView,
  );
  const externalResources = (
    data?.articlesAsExternalFiles
      ?.map((x) => ({
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        ...(x as IResourceWithThumbnailData),
        url: x.fileToLinkTo?.url,
        resourceType: ResourceType.Documents,
        uniqueId: x.uniqueIdentifierForClickTracking,
      }))
      .concat(
        data?.articlesAsExternalLinks?.map((x) => ({
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          ...(x as IResourceWithThumbnailData),
          url: x.urlToLinkTo,
          resourceType: x.resourceType,
          uniqueId: x.uniqueIdentifierForClickTracking,
        })) ?? [],
      ) ?? []
  ).filter(
    ({ authenticationRequiredToView }) =>
      atLeastHalfMagicLoggedIn || !authenticationRequiredToView,
  );

  return (
    <>
      {articleTypeArticles.map((articleData) => (
        <ArticlePreviewCard
          key={
            articleData.identifier ||
            articleData.urlTitle ||
            articleData.videoUrl
          }
          renderCardWrapper={renderCardWrapper}
          article={articleData}
          articleLink={articleLink ?? { text: learnMoreLinkText ?? '' }}
        />
      ))}
      {externalResources.map((externalResource) => (
        <ResourcePreviewCard
          dataElementId={DataElementId.RESOURCE_LIB_FEATURED_ARTICLE_CARD}
          uniqueId={externalResource.uniqueId}
          key={externalResource?.url}
          renderCardWrapper={renderCardWrapper}
          resourceType={externalResource.resourceType}
          resourceLink={
            externalResource?.url
              ? {
                linkText: learnMoreLinkText ?? '-',
                linkTo: externalResource?.url,
                dataElementId:
                    DataElementId.RESOURCE_LIB_FEATURED_ARTICLE_READ_MORE,
              }
              : undefined
          }
          media={{
            url:
              externalResource?.thumbnail?.url ??
              match(externalResource.resourceType)
                .with(ResourceType.Video, () => externalResource?.url)
                .with(ResourceType.Documents, () => documentsThumbnail)
                .otherwise(() => undefined) ??
              '',
            type:
              !externalResource?.thumbnail?.url &&
              externalResource.resourceType === ResourceType.Video
                ? MediaType.Video
                : MediaType.Image,
          }}
          title={externalResource?.titleText}
          description={externalResource?.descriptionText}
        />
      ))}
    </>
  );
};

export default ResourcesPreviewCards;
